import React, { useState, useCallback } from 'react';
import styled from 'styled-components';
import OAuthInput from '../OAuthFlow/shared/OAuthInput';
import OAuthButton from '../OAuthFlow/shared/OAuthButton';

const Heading = styled.h1`
  font-size: 24px;
  font-weight: 500;
  color: ${p => p.theme.text};
  text-align: center;
  margin-bottom: 8px;
  line-height: 32px;
`;

const Subtext = styled.p`
  font-size: 15px;
  font-weight: 400;
  color: ${p => p.theme.textSecondary};
  text-align: center;
  line-height: 24px;
  margin-bottom: 24px;
`;

const LinkButton = styled.button`
  display: block;
  margin: 16px auto 0;
  background: none;
  border: none;
  padding: 4px 8px;
  color: ${p => p.theme.accent || p.theme.text};
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  &:disabled { opacity: 0.5; cursor: default; }
`;

const MethodList = styled.div`
  margin-top: 16px;
  border: 1px solid ${p => p.theme.border};
  border-radius: ${p => p.theme.radiusLg || '12px'};
  overflow: hidden;
`;

const MethodRow = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  width: 100%;
  padding: 14px 16px;
  background: ${p => p.theme.surface};
  border: none;
  border-top: 1px solid ${p => p.theme.border};
  color: ${p => p.theme.text};
  font-size: 15px;
  text-align: left;
  cursor: pointer;
  &:first-child { border-top: none; }
  &:hover { background: ${p => p.theme.surface2 || p.theme.surface}; }
  &:disabled { cursor: default; }
`;

const MethodInfo = styled.span`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;

const MethodName = styled.span`
  color: ${p => p.theme.text};
  font-size: 15px;
`;

const MethodMeta = styled.span`
  color: ${p => p.theme.textSecondary};
  font-size: 13px;
`;

const CurrentBadge = styled.span`
  flex-shrink: 0;
  padding: 2px 8px;
  border-radius: 999px;
  background: ${p => p.theme.surface2 || p.theme.border};
  color: ${p => p.theme.textSecondary};
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
`;

const Confirmation = styled.p`
  margin-top: 16px;
  text-align: center;
  color: ${p => p.theme.textSecondary};
  font-size: 14px;
  line-height: 20px;
`;

// A normalized MFA channel is { kind: email|phone|authenticator, target, mfaId }.
// `target` is Rivian's already-masked destination (email / phone) or, for an
// authenticator, its nickname.
function methodName(channel) {
  if (!channel) return 'Verification code';
  if (channel.kind === 'email') return 'Email';
  if (channel.kind === 'phone') return 'Text message';
  if (channel.kind === 'authenticator') return 'Authenticator app';
  return 'Verification code';
}

// Masked destination / authenticator nickname shown under the method name.
function channelDetail(channel) {
  return channel && channel.target ? channel.target : null;
}

// The instruction adapts to HOW the code is obtained: a phone/email code is
// *sent*, an authenticator code is *generated* by the user's app.
function instruction(channel) {
  if (!channel) return 'Rivian just sent you a verification code. Enter it to finish signing in.';
  const t = channel.target;
  if (channel.kind === 'phone') return `Enter the verification code Rivian texted${t ? ` to ${t}` : ''}.`;
  if (channel.kind === 'email') return `Enter the verification code Rivian emailed${t ? ` to ${t}` : ''}.`;
  if (channel.kind === 'authenticator') return `Enter the current code from your authenticator app${t ? ` (${t})` : ''}.`;
  return 'Enter your verification code to finish signing in.';
}

function resentMessage(channel) {
  if (channel && channel.kind === 'phone') return 'A new code has been texted to you.';
  if (channel && channel.kind === 'email') return 'A new code is on its way to your email.';
  return 'A new code is on its way.';
}

// Rivian's MFA code (email, SMS, or authenticator, per the account's settings).
// Posted plain to the backend, which relays it to Rivian's loginWithOTPV2. Not
// the boxed 6-digit OtpInput used by the email-OTP flow — Rivian code length varies.
export default function OtpStep({ headers, onComplete, targetChannel, channels, onTargetChange }) {
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showMethods, setShowMethods] = useState(false);
  const [switching, setSwitching] = useState(false);
  const [pendingMfaId, setPendingMfaId] = useState(null);
  const [resent, setResent] = useState(false);

  const availableChannels = channels || [];
  const hasChoice = availableChannels.length > 1;
  // An authenticator code is generated on-device, so there's nothing to resend.
  const isAuthenticator = !!(targetChannel && targetChannel.kind === 'authenticator');

  const handleSubmit = useCallback(async function (e) {
    e.preventDefault();
    // Strip ALL whitespace — authenticator apps commonly display codes as
    // "123 456", and a space would fail Rivian's exact-match verify.
    const normalized = code.replace(/\s+/g, '');
    if (!normalized) {
      setError('Enter the code Rivian sent you.');
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const response = await fetch('/oauth/rivian/otp', {
        method: 'POST',
        headers: headers(),
        body: JSON.stringify({ otp: normalized }),
      });
      const data = await response.json();

      if (response.ok && data.status === 'complete') {
        onComplete(data.redirect_to);
        return;
      }
      setError(data.message || 'That code did not work. Please try again.');
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [code, headers, onComplete]);

  // POST a resend. `channel === null` resends to the CURRENT target (mfa_id
  // omitted — the backend leaves the destination unchanged); a channel switches
  // the destination. Works for single-channel accounts (which have no picker).
  const postResend = useCallback(async function (channel) {
    setSwitching(true);
    setPendingMfaId(channel ? channel.mfaId : null);
    setError(null);
    setResent(false);
    try {
      const response = await fetch('/oauth/rivian/resend', {
        method: 'POST',
        headers: headers(),
        body: JSON.stringify(channel ? { mfa_id: channel.mfaId } : {}),
      });
      const data = await response.json();

      if (response.ok && data.status === 'otp_sent') {
        if (channel && onTargetChange) onTargetChange(channel);
        setShowMethods(false);
        setCode('');
        setResent(true);
        return;
      }
      setError(data.message || 'We could not send a new code. Please try again.');
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setSwitching(false);
      setPendingMfaId(null);
    }
  }, [headers, onTargetChange]);

  return (
    <form onSubmit={handleSubmit}>
      <Heading>Enter your code</Heading>
      <Subtext>{instruction(targetChannel)}</Subtext>

      <OAuthInput
        label="Verification code"
        type="text"
        inputMode="numeric"
        placeholder="123456"
        value={code}
        onChange={(e) => { setCode(e.target.value); setError(null); }}
        error={error}
        autoFocus
        autoComplete="one-time-code"
      />

      <OAuthButton type="submit" loading={loading} disabled={!code.trim()}>
        Verify &amp; Continue
      </OAuthButton>

      {resent && !isAuthenticator && <Confirmation>{resentMessage(targetChannel)}</Confirmation>}

      {!isAuthenticator && (
        <LinkButton type="button" onClick={() => postResend(null)} disabled={loading || switching}>
          {switching && !showMethods ? 'Sending…' : "Didn't get a code? Resend"}
        </LinkButton>
      )}

      {hasChoice && !showMethods && (
        <LinkButton type="button" onClick={() => setShowMethods(true)} disabled={loading || switching}>
          Use a different method
        </LinkButton>
      )}

      {hasChoice && showMethods && (
        <MethodList>
          {availableChannels.map((channel) => {
            const isCurrent = targetChannel && channel.mfaId === targetChannel.mfaId;
            const isPending = switching && pendingMfaId === channel.mfaId;
            const detail = channelDetail(channel);
            return (
              <MethodRow
                type="button"
                key={channel.mfaId}
                onClick={() => postResend(channel)}
                disabled={switching || isCurrent}
              >
                <MethodInfo>
                  <MethodName>{methodName(channel)}</MethodName>
                  {detail && <MethodMeta>{detail}</MethodMeta>}
                </MethodInfo>
                {isPending
                  ? <MethodMeta>Sending…</MethodMeta>
                  : isCurrent && <CurrentBadge>Current</CurrentBadge>}
              </MethodRow>
            );
          })}
        </MethodList>
      )}
    </form>
  );
}
